/**
 * Writing a new prompt.
 *
 * Creating one used to be a picker and a name box, which produced an empty
 * prompt and a second step - go and find it, open it, write the instruction.
 * Two problems with that. The obvious one is the step people forget, leaving
 * rows in the catalogue that do nothing. The quieter one is that it framed
 * "create a prompt" as filling in metadata, when the only thing that matters
 * about a prompt is the instruction inside it.
 *
 * So this is the same authoring screen as editing, with two fields in front of
 * it. It opens BLANK - see the note by useState below for why - and the shipped
 * wording is one click away as chips, section by section, rather than typed in
 * already and waiting to be half-deleted.
 *
 * One save creates the prompt and its first version, live. It affects nobody
 * until it is either the default for its generation or assigned to a clinic on
 * that clinic's own page - which is where that decision belongs, next to the
 * rest of that clinic's setup.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router';
import { ApiError, post, type ListResponse } from '../api.ts';
import { Card, Pill } from '../components/ui.tsx';
import { PromptComposer } from '../components/PromptComposer.tsx';

/** What one generation starts from, as the server compiled it. */
export interface PromptDefault {
  taskType: string;
  system: string;
  userTemplate: string;
  templatable: boolean;
  variables: string[];
}

const USE_LABEL: Record<string, string> = {
  section: '節ごとの執筆（本文生成）',
  proposals: 'タイトル・見出し案',
  outline: '構成案',
  image: 'ペルソナの人物写真',
  article_image: '記事の画像（カバー・本文中）',
};

/*
 * Whole-article generation is not offered here any more.
 *
 * Every article is written section by section; `article.body` is the older path,
 * kept alive because prompts written for it still exist and the code still
 * resolves them. Offering it as a choice for a NEW prompt invites somebody to
 * write an instruction for a route nothing takes - which then sits in the
 * catalogue looking assignable.
 *
 * Not deleted from the server: an existing body prompt keeps working and stays
 * visible on the list, and removing the task type would be a migration over
 * stored rows for no gain. This is the front door, closed.
 */
const RETIRED_USES = ['body'];

/** Both picture uses: the whole instruction is the template. See migration 093. */
const isImageUse = (use: string) => use === 'image' || use === 'article_image';

interface Finding {
  level: 'error' | 'warn';
  code: string;
  message: string;
}

export default function AdminPromptNew() {
  const { defaults } = useLoaderData<{ defaults: ListResponse<PromptDefault> }>();
  const navigate = useNavigate();

  const [use, setUse] = useState('section');
  const [name, setName] = useState('');
  const [findings, setFindings] = useState<Finding[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shipped = useMemo(
    () => defaults.items.find((entry) => entry.taskType === use) ?? defaults.items[0]!,
    [defaults.items, use],
  );

  /*
   * Blank, not prefilled.
   *
   * This used to open with the shipped wording already typed in, on the theory
   * that a first instruction should start from the floor rather than from
   * nothing. In practice that meant every new prompt looked identical to the
   * default until somebody deleted most of it - which is a worse way to learn
   * what the floor even is than reading it.
   *
   * The floor is still one click away: PromptComposer offers every shipped
   * section as a chip, each carrying its reviewed wording, and the opening
   * role-and-reader paragraph is offered the same way for the box that has no
   * heading to list it under. So starting blank costs nothing and stops a new
   * prompt masquerading as a considered rewrite of the default.
   */
  const [body, setBody] = useState('');
  const [template, setTemplate] = useState('');

  /*
   * Linted on the server as it is typed - the same function that decides whether
   * the save is refused, so the panel cannot disagree with the rule. By task
   * type rather than by prompt id: there is no prompt yet.
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      void post<{ findings: Finding[] }>('/admin/prompt-lint', {
        taskType: use,
        body,
        userTemplate: template,
      })
        .then((result) => setFindings(result.findings))
        .catch(() => {
          /* A failed lint is not an authoring error; the save still checks. */
        });
    }, 400);
    return () => clearTimeout(timer);
  }, [use, body, template]);

  const hasError = findings.some((finding) => finding.level === 'error');

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await post<{ id: string }>('/admin/prompts', {
        /*
         * Derived from the generation, not typed. The key has to match a narrow
         * pattern, it is never seen by a reader, and an operator asked to invent
         * one types the Japanese name into it and gets a validation error.
         */
        key: `article.${use}.v${Date.now().toString(36)}`,
        name: name.trim(),
        taskType: use,
        body,
        userTemplate: shipped.templatable ? template : '',
        note: '新規作成',
      });
      void navigate(`/admin/prompts/${created.id}`);
    } catch (err) {
      setError(
        err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : '作成できませんでした',
      );
      setBusy(false);
    }
  }

  return (
    <Card
      title="新しいプロンプト"
      caption="指示文を書いて保存すると、その時点から使えるようになります。"
      actions={
        <Link className="btn btn--sm btn--ghost" to="/admin/prompts">
          一覧に戻る
        </Link>
      }
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      <div className="grid grid--2">
        <div className="field">
          <label htmlFor="np-use">対象の生成</label>
          <select
            id="np-use"
            className="input"
            value={use}
            onChange={(event) => setUse(event.target.value)}
          >
            {defaults.items
              .filter((entry) => !RETIRED_USES.includes(entry.taskType))
              .map((entry) => (
                <option key={entry.taskType} value={entry.taskType}>
                  {USE_LABEL[entry.taskType] ?? entry.taskType}
                </option>
              ))}
          </select>
          <span className="field__hint">
            どの生成に使う指示文かを決めます。あとから変えられないので、ここだけ先に選びます。
          </span>
        </div>

        <div className="field">
          <label htmlFor="np-name">プロンプト名</label>
          <input
            id="np-name"
            maxLength={200}
            value={name}
            placeholder="例：初診の方向け・やわらかい文体"
            onChange={(event) => setName(event.target.value)}
          />
          <span className="field__hint">
            クリニックに割り当てるときに選ぶ名前です。誰に向けた指示文かが分かる名前にします。
          </span>
        </div>
      </div>

      <PromptComposer
        label={isImageUse(use) ? '指示文（画像の作り方）' : '指示文（システムプロンプト）'}
        value={body}
        onChange={setBody}
        shipped={shipped.system}
        variables={isImageUse(use) ? shipped.variables : []}
        hint={
          isImageUse(use)
            ? '空欄から書き始めます。上のチップで、タイトルや場面の説明を挿入できます。文字入り・ビフォーアフター・実在人物の描写は常に禁止されます。'
            : '空欄から書き始めます。既定のセクションは下の「＋」ボタンで、そのままの文面を追加できます。'
        }
      />

      {shipped.templatable && (
        <PromptComposer
          label="依頼文"
          value={template}
          onChange={setTemplate}
          shipped={shipped.userTemplate}
          variables={shipped.variables}
          hint="空欄から書き始めます。下の「＋ 依頼」で既定の文面を、上のチップで登録済みの情報を挿入できます。この下に「承認済み情報」が自動で付きます（差し替え不可）。"
        />
      )}

      <p className="form__section">チェック</p>
      {findings.length === 0 ? (
        <p className="muted small" style={{ margin: '6px 0 0' }}>
          <Pill tone="ok">問題なし</Pill> 指示の内容に不足はありません。
        </p>
      ) : (
        <ul className="findings">
          {findings.map((finding) => (
            <li key={finding.code} className={`findings__item findings__item--${finding.level}`}>
              <Pill tone={finding.level === 'error' ? 'danger' : 'warn'}>
                {finding.level === 'error' ? '保存不可' : '確認'}
              </Pill>
              <span>{finding.message}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="editpanel__actions">
        <button
          className="btn btn--sm"
          type="button"
          disabled={busy || hasError || name.trim() === '' || body.trim() === ''}
          onClick={() => void create()}
        >
          {busy ? '作成中…' : '作成して使えるようにする'}
        </button>
      </div>
      <p className="muted small" style={{ marginBottom: 0 }}>
        作成した時点では、どのクリニックにも影響しません。使うクリニックは、クリニック詳細画面の
        「使用するプロンプト」で選びます。
      </p>
    </Card>
  );
}
